import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { X } from 'lucide-react';
import ReactSelect from 'react-select';
import menuApi from 'api/requests/menu';
import { getInventoryItems } from 'api/requests/inventory';
import colors from 'theme/colors';

export default function OptionForm({ slug, initialData, onSaved, onCancel }) {
    const [name, setName] = useState(initialData?.name || '');
    const [priceAdjustment, setPriceAdjustment] = useState(initialData?.price_adjustment ?? '0');
    const [allInventoryItems, setAllInventoryItems] = useState([]);
    const [techSheets, setTechSheets] = useState([]);
    const [selectedInventoryOption, setSelectedInventoryOption] = useState(null);
    const [techQuantity, setTechQuantity] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        getInventoryItems(slug)
            .then(items => setAllInventoryItems(items || []))
            .catch(() => {});
        menuApi.getOptionTechSheets(slug, initialData.id)
            .then(({ data }) => setTechSheets(
                (data.tech_sheets || []).map(t => ({
                    inventory_item_id: t.inventory_item_id,
                    quantity_used: Number(t.quantity_used),
                }))
            ))
            .catch(() => setTechSheets([]));
    }, [slug, initialData.id]);

    function addTechSheetRow() {
        if (!selectedInventoryOption) return;
        const qty = Number(techQuantity);
        if (!qty || qty <= 0) {
            setError('Informe uma quantidade válida para o insumo.');
            return;
        }
        setTechSheets(prev => [...prev, { inventory_item_id: selectedInventoryOption.value, quantity_used: qty }]);
        setSelectedInventoryOption(null);
        setTechQuantity('');
        setError(null);
    }

    function removeTechSheetRow(itemId) {
        setTechSheets(prev => prev.filter(t => t.inventory_item_id !== itemId));
    }

    const inventoryOptions = allInventoryItems
        .filter(i => !techSheets.some(t => t.inventory_item_id === i.id))
        .map(i => ({ value: i.id, label: `${i.name} (${i.unit_of_measure})` }));

    async function handleSubmit(e) {
        e.preventDefault();
        if (!name.trim()) {
            setError('Nome é obrigatório');
            return;
        }
        setLoading(true);
        setError(null);
        try {
            await menuApi.updateOption(initialData.id, {
                name: name.trim(),
                price_adjustment: Number(priceAdjustment) || 0,
            });
            const { data: synced } = await menuApi.syncOptionTechSheets(slug, initialData.id, techSheets);
            onSaved(synced);
        } catch (err) {
            setError('Erro ao salvar. Tente novamente.');
        } finally {
            setLoading(false);
        }
    }

    return (
        <Form onSubmit={handleSubmit}>
            <Field>
                <Label>Nome *</Label>
                <Input
                    value={name}
                    onChange={e => setName(e.target.value)}
                    placeholder='Ex: Hambúrguer extra'
                    autoFocus
                />
            </Field>

            <Field>
                <Label>Preço adicional</Label>
                <PriceWrapper>
                    <CurrencyPrefix>+R$</CurrencyPrefix>
                    <PriceInput
                        type='number'
                        step='0.01'
                        min='0'
                        value={priceAdjustment}
                        onChange={e => setPriceAdjustment(e.target.value)}
                        placeholder='0,00'
                    />
                </PriceWrapper>
            </Field>

            <Field>
                <Label>Ficha técnica (insumos)</Label>
                {techSheets.length > 0 && (
                    <TechSheetList>
                        {techSheets.map(t => {
                            const invItem = allInventoryItems.find(i => i.id === t.inventory_item_id);
                            return (
                                <TechSheetRow key={t.inventory_item_id}>
                                    <TechSheetInfo>
                                        {invItem
                                            ? `${invItem.name} — ${t.quantity_used} ${invItem.unit_of_measure}`
                                            : `Insumo removido — ${t.quantity_used}`}
                                    </TechSheetInfo>
                                    <RemoveRowBtn type='button' onClick={() => removeTechSheetRow(t.inventory_item_id)}>
                                        <X size={14} />
                                    </RemoveRowBtn>
                                </TechSheetRow>
                            );
                        })}
                    </TechSheetList>
                )}
                <TechSheetAddRow>
                    <ReactSelectWrapper>
                        <ReactSelect
                            placeholder='Selecionar insumo'
                            options={inventoryOptions}
                            value={selectedInventoryOption}
                            onChange={setSelectedInventoryOption}
                        />
                    </ReactSelectWrapper>
                    <Input
                        type='number'
                        step='0.0001'
                        min='0'
                        style={{ flex: 1 }}
                        placeholder={selectedInventoryOption
                            ? `Qtd. (${allInventoryItems.find(i => i.id === selectedInventoryOption.value)?.unit_of_measure || ''})`
                            : 'Qtd.'}
                        value={techQuantity}
                        onChange={e => setTechQuantity(e.target.value)}
                    />
                    <AddRowBtn type='button' onClick={addTechSheetRow}>Adicionar</AddRowBtn>
                </TechSheetAddRow>
            </Field>

            {error && <ErrorText>{error}</ErrorText>}

            <ButtonRow>
                {onCancel && (
                    <CancelBtn type='button' onClick={onCancel} disabled={loading}>
                        Cancelar
                    </CancelBtn>
                )}
                <SubmitBtn type='submit' disabled={loading}>
                    {loading ? 'Salvando...' : 'Salvar alterações'}
                </SubmitBtn>
            </ButtonRow>
        </Form>
    );
}

const Form = styled.form`display: flex; flex-direction: column; gap: 16px;`;
const ButtonRow = styled.div`display: flex; gap: 10px; justify-content: flex-end;`;
const Field = styled.div`display: flex; flex-direction: column; gap: 6px;`;
const Label = styled.label`font-size: 13px; font-weight: 600; color: ${colors.nightDark};`;
const Input = styled.input`
    border: 1.5px solid ${colors.neutralDark}; border-radius: 10px; padding: 12px 14px;
    font-size: 15px; outline: none; color: ${colors.nightDark}; background: #fff;
    transition: border-color 0.15s;
    &:focus { border-color: ${colors.occasionalPurple}; }
    &::placeholder { color: #999; }
`;
const PriceWrapper = styled.div`
    display: flex; align-items: center;
    border: 1.5px solid ${colors.neutralDark}; border-radius: 10px; background: #fff;
    overflow: hidden; transition: border-color 0.15s;
    &:focus-within { border-color: ${colors.occasionalPurple}; }
`;
const CurrencyPrefix = styled.span`
    padding: 12px 10px 12px 14px; font-size: 15px; color: #666;
    font-weight: 600; border-right: 1px solid ${colors.neutralDark}; background: ${colors.neutralDark};
`;
const PriceInput = styled.input`
    flex: 1; border: none; padding: 12px 14px; font-size: 15px; outline: none;
    color: ${colors.nightDark}; background: transparent;
`;
const TechSheetList = styled.div`display: flex; flex-direction: column; gap: 6px; margin-bottom: 10px;`;
const TechSheetRow = styled.div`
    display: flex; align-items: center; justify-content: space-between; gap: 10px;
    padding: 10px 12px; border-radius: 10px; background: ${colors.neutralBackground || '#f9f9f9'};
    border: 1px solid ${colors.neutralDark};
`;
const TechSheetInfo = styled.span`font-size: 13px; font-weight: 500; color: ${colors.nightDark};`;
const RemoveRowBtn = styled.button`
    width: 22px; height: 22px; border-radius: 50%; flex-shrink: 0;
    background: rgba(0,0,0,0.08); color: ${colors.night}; border: none;
    display: flex; align-items: center; justify-content: center; cursor: pointer;
    &:hover { background: ${colors.danger}20; color: ${colors.danger}; }
`;
const TechSheetAddRow = styled.div`display: flex; gap: 8px; align-items: center;`;
const ReactSelectWrapper = styled.div`flex: 2;`;
const AddRowBtn = styled.button`
    flex-shrink: 0; padding: 12px 16px; border-radius: 10px; border: none;
    background: ${colors.occasionalPurple}; color: #fff; font-size: 14px; font-weight: 600;
    cursor: pointer; transition: opacity 0.15s;
    &:hover { opacity: 0.9; }
`;
const ErrorText = styled.p`color: ${colors.danger}; font-size: 13px; margin: 0;`;
const SubmitBtn = styled.button`
    padding: 14px; border-radius: 10px;
    background: ${({ disabled }) => disabled ? colors.neutralDark : colors.occasionalPurple};
    color: ${({ disabled }) => disabled ? colors.nightLight : '#fff'};
    font-size: 15px; font-weight: 700; border: none;
    cursor: ${({ disabled }) => disabled ? 'not-allowed' : 'pointer'};
    &:hover:not(:disabled) { opacity: 0.9; }
`;
const CancelBtn = styled.button`
    padding: 14px 20px; border-radius: 10px;
    background: none; border: 1.5px solid ${colors.neutralDark};
    color: ${colors.nightDark}; font-size: 15px; font-weight: 600;
    cursor: ${({ disabled }) => disabled ? 'not-allowed' : 'pointer'};
    transition: background 0.15s;
    &:hover:not(:disabled) { background: ${colors.neutralDark}; }
`;
